import React from 'react'
import './unitInfo.scss'

// The unit's monthly climate chart (WEB-7147, WEB-7149): average highs and lows as bars, record highs and
// lows as light lines, the chance of a wet day and of snow on the ground under each month, and the hunt
// season months shaded. Drawn in Hunt Planner's Season weather and on Artemis's unit_climate card.

const MONTHS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D']
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const r = (v) => Math.round(v)

// A month's figures on hover: "Oct: average 59° / 31°, records 89° / −11°, 1.2 in of rain and snow, …"
const monthTitle = (m) => [
  `${MONTH_NAMES[m.month - 1]}: average ${r(m.avgHigh)}° / ${r(m.avgLow)}°`,
  Number.isFinite(m.recordHigh) && Number.isFinite(m.recordLow) && `records ${r(m.recordHigh)}° / ${r(m.recordLow)}°`,
  Number.isFinite(m.precip) && `${m.precip.toFixed(1)} in of rain and snow`,
  Number.isFinite(m.precipChance) && `${r(m.precipChance)}% chance of a wet day`,
  Number.isFinite(m.snowChance) && `${r(m.snowChance)}% chance of snow on the ground`,
  Number.isFinite(m.snowDepth) && m.snowChance > 0 && `about ${r(m.snowDepth)} in when there is`,
].filter(Boolean).join(', ')

// A light line through each month's record high, and one through the record lows, with a dot per month
// ("M x,y L x,y ..."); a month without a record breaks the line.
const recordPath = (points) => points
  .map((p, i) => (p ? `${i && points[i - 1] ? 'L' : 'M'}${p[0]},${p[1]}` : ''))
  .filter(Boolean).join(' ')

// Monthly average highs and lows (a bar from low to high), the record highs and lows as light lines across
// the months (their values on hover), the chance of a wet day and of snow on the ground under each month,
// and the hunt season months shaded.
export const ClimateChart = ({ months, highlight }) => {
  // Twelve columns across the width, or the few months asked for centred at a twelve-month column's width or
  // wider (a sixth of the chart at most), so two months don't become two slabs.
  const cols = Math.max(1, months.length)
  const W = 560, H = 214, top = 22, bottom = 150
  const colW = (W - 20) / Math.max(cols, 6)
  const left = (W - colW * cols) / 2
  const temps = months.flatMap((m) => [m.avgHigh, m.avgLow, m.recordHigh, m.recordLow]).filter((v) => Number.isFinite(v))
  if (!temps.length) return null
  const lo = Math.floor(Math.min(...temps) / 10) * 10
  const hi = Math.ceil(Math.max(...temps) / 10) * 10
  const y = (t) => bottom - ((t - lo) / (hi - lo || 1)) * (bottom - top)
  const cx = (i) => left + i * colW + colW / 2
  const point = (key) => months.map((m, i) => (Number.isFinite(m[key]) ? [cx(i), y(m[key])] : null))
  const highs = point('recordHigh'), lows = point('recordLow')

  return (
    <svg className="climate-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Monthly average and record high and low temperatures">
      {months.map((m, i) => highlight.has(m.month) && (
        <rect key={m.month} x={left + i * colW + 1} y={4} width={colW - 2} height={H - 6} rx={4} className="climate-season" />
      ))}
      <path d={recordPath(highs)} className="climate-record" />
      <path d={recordPath(lows)} className="climate-record" />
      {months.map((m, i) => {
        const x = left + i * colW
        const cx = x + colW / 2
        return (
          <g key={m.month} className="climate-month-group">
            <title>{monthTitle(m)}</title>
            <rect x={x} y={0} width={colW} height={H} className="climate-hit" />
            {highs[i] && <circle cx={cx} cy={highs[i][1]} r={2} className="climate-record-dot" />}
            {lows[i] && <circle cx={cx} cy={lows[i][1]} r={2} className="climate-record-dot" />}
            {Number.isFinite(m.avgHigh) && Number.isFinite(m.avgLow) && (
              <rect x={cx - 7} y={y(m.avgHigh)} width={14} height={Math.max(2, y(m.avgLow) - y(m.avgHigh))} rx={3} className="climate-bar" />
            )}
            {Number.isFinite(m.avgHigh) && <text x={cx} y={y(m.avgHigh) - 5} className="climate-high">{Math.round(m.avgHigh)}°</text>}
            {Number.isFinite(m.avgLow) && <text x={cx} y={y(m.avgLow) + 13} className="climate-low">{Math.round(m.avgLow)}°</text>}
            {/* record values: shown while the month is hovered */}
            {highs[i] && <text x={cx} y={highs[i][1] - 5} className="climate-record-value">{r(m.recordHigh)}°</text>}
            {lows[i] && <text x={cx} y={lows[i][1] + 12} className="climate-record-value">{r(m.recordLow)}°</text>}
            <text x={cx} y={bottom + 26} className="climate-month">{MONTHS[m.month - 1]}</text>
            {Number.isFinite(m.precipChance) && <text x={cx} y={bottom + 43} className="climate-precip">{r(m.precipChance)}%</text>}
            {Number.isFinite(m.snowChance) && <text x={cx} y={bottom + 59} className="climate-snow">{r(m.snowChance)}%</text>}
          </g>
        )
      })}
    </svg>
  )
}
